/**
 * PortlessManifestCard — manual "send now" for the daily Portless inbound
 * packing-list export. Lives under Settings → Advanced → Portless (next to the
 * Portless credentials). A daily cron sends the same manifests automatically;
 * this lets an operator send on demand for an ad-hoc pickup.
 *
 * Admin / Warehouse-Coordinator only (enforced backend-side on
 * portlessExport.runNow). Emails customer data to Portless, so the send is
 * behind a confirm dialog that shows the order counts.
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileSpreadsheet, Loader2, Send } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { format } from 'date-fns';

function orderWord(n: number): string {
  return n === 1 ? 'order' : 'orders';
}

function lineWord(n: number): string {
  return n === 1 ? 'line' : 'lines';
}

export function PortlessManifestCard() {
  const [open, setOpen] = useState(false);
  const pendingQuery = trpc.portlessExport.getPending.useQuery();

  const runNow = trpc.portlessExport.runNow.useMutation({
    onSuccess: (res) => {
      const sent: string[] = [];
      if (res.crossdock === 'sent') sent.push(`cross-dock (${res.crossdockCount})`);
      if (res.hybrid === 'sent') sent.push(`hybrid (${res.hybridCount})`);
      // runNow can now span several Portless locations, so a partition can come
      // back 'failed' while another sent. Before, sent+failed was unreachable and
      // any non-'sent' outcome meant "nothing to send" — keeping that shape would
      // report a genuine failed send to a third party as reassuring info.
      const failed = res.crossdock === 'failed' || res.hybrid === 'failed';
      // Both partitions 'skipped' means there WAS volume but the send was
      // refused (a partition with no lines reports 'empty') — i.e. no manifest
      // recipient configured. Do not report that as "nothing to send".
      const configSkipped = res.crossdock === 'skipped' && res.hybrid === 'skipped';
      if (configSkipped) {
        toast.error(
          'Manifest recipient is not configured. Set it in Settings before sending; nothing was sent.',
          { duration: Infinity }
        );
      } else if (failed) {
        toast.error(
          sent.length
            ? `Portless manifest partly failed. Sent: ${sent.join(', ')}. At least one manifest did not send, check the logs before re-running.`
            : 'Portless manifest failed to send. Check the logs before re-running.',
          { duration: Infinity }
        );
      } else if (sent.length) {
        toast.success(`Portless manifest sent: ${sent.join(', ')}`);
      } else {
        toast.info('Nothing to send. No Portless_ready-tagged orders shipped in the last 24 hours.');
      }
      setOpen(false);
      void pendingQuery.refetch();
    },
    onError: (err) => {
      // Persist the error toast (DESIGN.md §15.5) — a failed send to a third
      // party should not silently disappear.
      toast.error(`Failed to send manifest: ${err.message}`, { duration: Infinity });
    },
  });

  const data = pendingQuery.data;
  const configured = data?.configured ?? false;
  const crossdockOrders = data?.crossdockOrders ?? 0;
  const hybridOrders = data?.hybridOrders ?? 0;
  const totalOrders = crossdockOrders + hybridOrders;
  // Lines Portless picks from its own warehouse rather than receiving inbound —
  // surfaced separately so the ready-to-send figure isn't read as all-inbound.
  const portlessStockUnits =
    (data?.crossdockPortlessUnits ?? 0) + (data?.hybridPortlessUnits ?? 0);
  const nothingToSend = configured && totalOrders === 0;
  const lastSentAt = data?.lastSentAt ?? null;
  const disabled = !configured || nothingToSend || pendingQuery.isLoading || runNow.isPending;

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">Portless daily manifest</CardTitle>
        </div>
        <CardDescription>
          Email Portless the inbound packing list of orders shipped from ThreadLab in the last 24 hours.
          The daily cron also sends this automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {!configured
            ? 'No Portless location is configured for this organization. Set a location’s shipping provider to Portless first.'
            : pendingQuery.isLoading
              ? 'Checking the last 24 hours…'
              : nothingToSend
                ? 'No Portless_ready-tagged orders shipped in the last 24 hours.'
                : `${crossdockOrders} cross-dock and ${hybridOrders} hybrid orders ready to send.`}
        </p>
        {configured && !pendingQuery.isLoading && !nothingToSend && portlessStockUnits > 0 && (
          <p className="text-sm text-muted-foreground">
            {/* "Of the SKU lines", not "Includes": the sentence above counts
                ORDERS, so "includes N lines" reads as N of those orders. */}
            {`Of the SKU lines on these manifests, ${portlessStockUnits} ${lineWord(portlessStockUnits)} Portless picks from its own stock (not inbound).`}
          </p>
        )}

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button disabled={disabled} className="gap-1.5">
              {runNow.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              Send Portless manifest
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send Portless manifest?</AlertDialogTitle>
              <AlertDialogDescription>
                This emails{' '}
                {crossdockOrders > 0 ? `a cross-dock list (${crossdockOrders} ${orderWord(crossdockOrders)})` : 'no cross-dock list'}
                {hybridOrders > 0 ? ` and a hybrid list (${hybridOrders} ${orderWord(hybridOrders)})` : ''} to Portless.
                Customer first names are included on the packing list.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={runNow.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  runNow.mutate({});
                }}
                disabled={runNow.isPending}
              >
                {runNow.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : null}
                Send
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {lastSentAt && (
          <p className="text-xs text-muted-foreground tabular-nums">
            Last sent: {format(new Date(lastSentAt), 'd MMM yyyy, HH:mm')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
